import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaExternalLinkAlt, FaImage, FaInfoCircle, FaStore, FaFilter, FaHeart, FaStar, FaCheckCircle } from 'react-icons/fa';
import '../../css/user-product-listings.css';

export const UserProductListings = ({ user, networkId }) => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);

  // Extract listings from user data
  const listings = user?.listings || [];

  useEffect(() => {
    // Extract unique categories from user's listings
    const uniqueCategories = [...new Set(
      listings.map(item =>
        item.productCategoryName || item.recordTypeName || 'Uncategorized'
      ).filter(Boolean)
    )];
    setCategories(uniqueCategories);
  }, [listings]);

  const filteredListings = selectedCategory === 'all'
    ? listings
    : listings.filter(item => {
        const category = item.productCategoryName || item.recordTypeName || 'Uncategorized';
        return category === selectedCategory;
      });

  const handleViewDetails = (product) => {
    // Use networkId if provided, fallback to 0 for global search, then 1 as last resort
    const networkPath = networkId !== undefined ? networkId : 0;
    navigate(`/product-detail/${networkPath}/${product.productListItemID}`);
  };

  if (listings.length === 0) {
    return (
      <div className="p-4">
        <div className="alert alert-info user-product-listings-alert-info" role="alert">
          <FaInfoCircle className="me-2" />
          This user has no product listings yet.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">
          <FaStore className="me-2 text-primary" />
          Product Listings ({listings.length})
        </h5>
      </div>

      {/* Category Filter Badges */}
      {categories.length > 0 && (
        <div className="mb-4">
          <div className="user-product-listings-filter-container">
            <h6 className="user-product-listings-filter-title">
              <FaFilter className="filter-icon" />
              Filter by Category
            </h6>
            <div className="d-flex flex-wrap gap-2">
              <button
                className={`btn ${selectedCategory === 'all' ? 'btn-primary' : 'btn-outline-primary'} user-product-listings-filter-button`}
                onClick={() => setSelectedCategory('all')}
              >
                All ({listings.length})
              </button>
              {categories.map(category => {
                const count = listings.filter(item => {
                  const itemCategory = item.productCategoryName || item.recordTypeName || 'Uncategorized';
                  return itemCategory === category;
                }).length;

                return (
                  <button
                    key={category}
                    className={`btn ${selectedCategory === category ? 'btn-primary' : 'btn-outline-primary'} user-product-listings-filter-button`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category} ({count})
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Product Listings Grid */}
      <div className="row g-4">
        {filteredListings.map(product => (
          <div key={product.productListItemID} className="col-lg-6 col-xl-4">
            <div className="user-product-listings-product-card">

              {/* Card Header with Image Background */}
              <div
                className="user-product-listings-card-header"
                style={{
                  background: product.productImageUrl
                    ? `linear-gradient(135deg, rgba(99, 102, 241, 0.7), rgba(139, 92, 246, 0.8)), url(${product.productImageUrl})`
                    : undefined
                }}
              >

                {/* Top Header - Logo and Badges */}
                <div className="user-product-listings-header-top">
                  {/* Product Logo */}
                  <div className="user-product-listings-product-logo">
                    {product.productLogoUrl ? (
                      <img
                        src={product.productLogoUrl}
                        alt={`${product.productName} Logo`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="user-product-listings-product-logo-placeholder" style={{ display: product.productLogoUrl ? 'none' : 'flex' }}>
                      <FaImage />
                    </div>
                  </div>

                  {/* Status Badges */}
                  <div className="user-product-listings-status-badges">
                    {product.isVerified && (
                      <span className="user-product-listings-badge user-product-listings-badge-verified">
                        <FaCheckCircle className="badge-icon" />
                        Verified
                      </span>
                    )}
                    {product.isHot && (
                      <span className="user-product-listings-badge user-product-listings-badge-hot">
                        <FaStar className="badge-icon" />
                        Hot
                      </span>
                    )}
                    {product.isFavorite && (
                      <span className="user-product-listings-badge user-product-listings-badge-favorite">
                        <FaHeart className="badge-icon" />
                        Favorite
                      </span>
                    )}
                    {product.isSponsored && (
                      <span className="user-product-listings-badge user-product-listings-badge-sponsored">
                        Sponsored
                      </span>
                    )}
                  </div>
                </div>

                {/* Bottom Header - Title and Category */}
                <div>
                  <h5 className="user-product-listings-product-title">
                    {product.productName}
                  </h5>
                  <div className="user-product-listings-category-badge">
                    {product.productCategoryName || product.recordTypeName || 'Uncategorized'}
                  </div>
                </div>

                {/* View Details Button - Floating in Header */}
                <button
                  onClick={() => handleViewDetails(product)}
                  className={`user-product-listings-view-details-button ${product.isVerified || product.isHot || product.isFavorite || product.isSponsored ? 'with-badges' : ''}`}
                >
                  <FaExternalLinkAlt className="button-icon" />
                  Details
                </button>
              </div>

              {/* Card Body */}
              <div className="user-product-listings-card-body">

                {/* Product Code */}
                <div className="user-product-listings-product-code-section">
                  <small className="user-product-listings-product-code-label">Product Code</small>
                  <code className="user-product-listings-product-code">
                    {product.productCode}
                  </code>
                </div>

                {/* Description */}
                <div className="user-product-listings-description-section">
                  {product.productShortDescription && (
                    <>
                      <small className="user-product-listings-description-label">Description</small>
                      <p className="user-product-listings-description-text">
                        {product.productShortDescription}
                      </p>
                    </>
                  )}
                </div>

                {/* Footer with Technical Info */}
                <div className="user-product-listings-card-footer">
                  <small className="user-product-listings-footer-info">
                    Product ID: {product.productListItemID}
                  </small>
                  <small className="user-product-listings-footer-info">
                    Owner Reg ID: {product.walletUserRegistrationID}
                  </small>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Results Count */}
      <div className="user-product-listings-results-count">
        Showing {filteredListings.length} of {listings.length} listings
        {selectedCategory !== 'all' && ` in "${selectedCategory}" category`}
      </div>
    </div>
  );
};